import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { client } from "@/services/client"
import type { Paths } from "@/services/types"
import { useQueryKey } from "@/services/utils"
import { fetchOrThrow } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { InsightsAggregate } from "./insights.types"
import { useWindowQuery } from "./use-window-query"
import type { WindowQuery } from "./use-window-query"

// insights-api models `organization_id` (required int) on the /api/v1/neat
// operations. `window`/`start`/`end` are honored by insights-api + forwarded by
// the BFF but not yet in the regenerated schema — extend locally (same pattern
// as `useGetVariance`'s `t_peer`).
type InsightsQuery = NonNullable<
  Paths["/api/v1/neat/insights"]["get"]["parameters"]["query"]
> &
  WindowQuery

const getInsights = async (
  organizationId: number,
  win: WindowQuery
): Promise<InsightsAggregate> => {
  const query: InsightsQuery = { organization_id: organizationId, ...win }

  // Aggregate homepage feed — every registered insight module's summary
  // tile, grouped by family. Generated schema types the body as an open
  // `{[key: string]: unknown}` record (module payloads vary), so it's a
  // typed pass-through cast (through `unknown` — the object-literal shape
  // doesn't "sufficiently overlap" `InsightsAggregate` for a direct cast),
  // same intent as `useGetVariance`.
  return (await fetchOrThrow(() =>
    client().GET("/api/v1/neat/insights", { params: { query } })
  )) as unknown as InsightsAggregate
}

export const useGetInsights = () => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const queryKey = useQueryKey(ApiQueryName.Insights, "getMany", null, win)
  return useQuery<InsightsAggregate>({
    queryKey,
    enabled: activeOrganizationId != null,
    queryFn: () => getInsights(Number(activeOrganizationId), win),
  })
}
